import { z } from "zod";

// Rollen — Postgres-Enum gespiegelt (siehe shared/schemas/db.ts)
export const USER_ROLES = ["superadmin", "admin", "pl", "buchhaltung"] as const;
export const userRoleSchema = z.enum(USER_ROLES);
export type UserRole = z.infer<typeof userRoleSchema>;

// User-Tiers — V5-Paritaet (Safe/Moderat/Performer/Freelancer/Fixlohn).
// text + zod-Enum, keine DB-CHECK; gleiche Disziplin wie bei object_type/status.
// Fixlohn = kein variabler Anteil (Buchhaltung, GF). Session 27 (F2) konsumiert
// die Werte fuer die Verdienst-Berechnung.
export const USER_TIERS = [
  "Safe",
  "Moderat",
  "Performer",
  "Freelancer",
  "Fixlohn",
] as const;
export const userTierSchema = z.enum(USER_TIERS);
export type UserTier = z.infer<typeof userTierSchema>;

// Objekttyp — gibt an, welche Calc-Engine-Variante (PV, BESS, WP, Charging, …)
// fuer ein Projekt gilt. DB-Spalte `projects.object_type` ist text (kein PG-
// Enum, kein CHECK). Session 21 erweitert um `charging` (Ladeinfrastruktur) —
// keine DB-Migration noetig, nur zod + UI.
// KOMBI-5: `kombi` = Multi-Gewerk-Projekt (mehrere Gewerk-Bloecke in EINER
// Kalkulation + EINEM Angebot, Phase K). KEIN eigener Seed — die Gewerke leben
// in den Calc-Bloecken (`seed.params.blocks`); die Multi-Auswahl im Anlegen-
// Wizard erzeugt sie. Vor `sonstiges` einsortiert (sonstiges bleibt der
// Empty-/Catch-all-Eintrag).
export const OBJECT_TYPES = ["pv", "bess", "wp", "charging", "kombi", "sonstiges"] as const;
export const objectTypeSchema = z.enum(OBJECT_TYPES);
export type ObjectType = z.infer<typeof objectTypeSchema>;

export const OBJECT_TYPE_LABELS: Record<ObjectType, string> = {
  pv: "Photovoltaik",
  bess: "Batteriespeicher (BESS)",
  wp: "Wärmepumpe",
  charging: "Ladeinfrastruktur",
  kombi: "Kombi-Anlage",
  sonstiges: "Sonstiges",
};

// ---------------------------------------------------------------------------
// Kundensprache (ENG-12) — projects.language, Migration 0099.
// ---------------------------------------------------------------------------
// Ursprung: Website-Formular (`lead_intakes.payload.metadata.locale`, seit
// WEB-6 de/en/it/zh), beim Lead-Intake auf das Projekt uebernommen; im
// Projekt-Edit aenderbar. Steuert die Kunden-Kommunikation: Offert-/AB-Mail,
// Portal-UI (Web-Repo liest das Feld aus dem Portal-DTO) und die Standard-
// Intro-/Schlusstexte der Offerte (ENG-15-Sprach-Regel: Freifeld leer =
// Standard-Vorlage in Projektsprache; gefuellt = PL-Text wortwoertlich).
// Interne Kommunikation (Admin-Alarme, OS-UI) bleibt deutsch.
export const CUSTOMER_LANGUAGES = ["de", "en", "it", "zh"] as const;
export const customerLanguageSchema = z.enum(CUSTOMER_LANGUAGES);
export type CustomerLanguage = z.infer<typeof customerLanguageSchema>;

export const CUSTOMER_LANGUAGE_LABELS: Record<CustomerLanguage, string> = {
  de: "Deutsch",
  en: "English",
  it: "Italiano",
  zh: "中文",
};

/**
 * Freier Locale-String ("de-CH", "it", "zh-CN", …) → CustomerLanguage.
 * Unbekannt/fehlend = de (gleiche Semantik wie LEAD-4).
 */
export function resolveCustomerLanguage(
  raw: string | null | undefined,
): CustomerLanguage {
  if (typeof raw !== "string") return "de";
  const base = raw.trim().toLowerCase().split("-")[0] ?? "";
  return (CUSTOMER_LANGUAGES as readonly string[]).includes(base)
    ? (base as CustomerLanguage)
    : "de";
}

// Kurz-Label fuer Produkt-Tiles + Auto-Titel. Spiegeln die OneDrive-Ordner-
// Konvention (BESS/PV/WP/LADE/KOMBI/SON).
export const OBJECT_TYPE_SHORT_LABELS: Record<ObjectType, string> = {
  pv: "PV",
  bess: "BESS",
  wp: "WP",
  charging: "LADE",
  kombi: "KOMBI",
  sonstiges: "SON",
};

// Baugruppen-Typ fuer Daniels OneDrive-Ordnerkonvention. Wird in Session 11
// Teil des Projekt-Codes (`<YYYY>_<NNNN>_<TYP>_<Name>[_<Ort>]`). In Session 10
// nur erfasst, noch nicht in den Code eingebaut.
export const BUILDING_TYPES = [
  "EFH",
  "MFH",
  "LAND",
  "STWEG",
  "MAI",
  "GEW",
] as const;
export const buildingTypeSchema = z.enum(BUILDING_TYPES);
export type BuildingType = z.infer<typeof buildingTypeSchema>;

export const BUILDING_TYPE_LABELS: Record<BuildingType, string> = {
  EFH: "Einfamilienhaus",
  MFH: "Mehrfamilienhaus",
  LAND: "Landwirtschaft",
  STWEG: "Stockwerkeigentum",
  MAI: "Mai",
  GEW: "Gewerbe",
};

// Projekt-Status — Text-Spalte + zod-Validation. State-Machine: shared/projects/statusMachine.ts.
// Phase 5.92: `lead_in_contact` sitzt zwischen `lead` und `offered` — PL hat
// den Kunden erstmals kontaktiert, aber noch keine Offerte rausgeschickt.
// Stufige Pipeline (Annehmen -> Kontaktiert -> Entscheiden) trackt die
// Vorphase, in der Sales-Disziplin entsteht.
export const PROJECT_STATUSES = [
  "lead",
  "lead_in_contact",
  "offered",
  "pending",
  "won",
  "in_progress",
  "completed",
  "rejected",
  "archived",
  // Phase 5.101 — Soft-Delete fuer Lead-Status. Status-Flip statt Hard-Delete,
  // Daten bleiben in DB (Recovery via Admin-DB-Edit moeglich). text + zod-
  // Enum, keine DB-CHECK — konsistent mit lead_in_contact-Pattern (5.92).
  // Erlaubte Quell-Status: lead, lead_in_contact (siehe statusMachine). Keine
  // Rueck-Transition (Recovery nur via SQL).
  "deleted",
] as const;
export const projectStatusSchema = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

// Offer-Status (Phase 4.2c-4 + Phase 4.2c-13). Versionierte Offerten pro
// Projekt — Status-Lifecycle: draft -> sent -> accepted -> confirmed
// (oder declined). text + zod-Enum (keine DB-CHECK), gleiche Konvention wie
// PROJECT_STATUSES. State-Machine wird in OfferService.updateStatus validiert.
//
// Phase 4.2c-13: `confirmed` ist der PL-bestaetigte Auftrag, der die bexio-
// Pipeline (Order + Invoice) ausloest. Status zwischen Kunden-Annahme
// (`accepted`, AB-Mail rausgegangen via 4.2c-12) und bexio-Buchhaltungs-Flow
// (4.2c-14). Terminal — kein Rueckweg, neue Verhandlung -> neue Draft-Version.
// `discarded` = FB-M1b (Marvin 2026-07-24): ein Entwurf, den der PL wieder
// wegraeumt. Kein Hard-Delete (Prinzip 14) — die Zeile bleibt mit ihrer
// V-Nummer stehen, verschwindet nur aus der Arbeits-Liste und kann via
// `discarded → draft` zurueckgeholt werden. Abgrenzung zu `declined`: das ist
// die Absage des KUNDEN an eine versendete Offerte, `discarded` ist die
// interne Korrektur eines nie versendeten Entwurfs.
export const OFFER_STATUSES = [
  "draft",
  "sent",
  "accepted",
  "confirmed",
  "declined",
  "discarded",
  // OFF-8: ein Entwurf, den ein juengerer Entwurf desselben Projekts ueberholt
  // hat — nur Bestand vor OFF-8 (Migration 0163), nie beim Kunden gewesen,
  // terminal. Seit OFF-8 gibt es hoechstens EINEN Entwurf pro Projekt.
  "superseded",
] as const;
export const offerStatusSchema = z.enum(OFFER_STATUSES);
export type OfferStatus = z.infer<typeof offerStatusSchema>;

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  draft: "Entwurf",
  sent: "Versendet",
  accepted: "Angenommen",
  confirmed: "Bestätigt",
  declined: "Abgelehnt",
  discarded: "Verworfen",
  superseded: "Überholt",
};

// OFF-10: die SORTE einer Offerten-Zeile (offers.kind). Der Richtpreis ist
// seither eine Zeile in `offers` — dieselbe Geschichte, derselbe Versand,
// dasselbe Portal wie die Offerte —, bleibt aber rechtlich ein Richtpreis
// (Art. 3 OR: «Offerte» ist ein bindender Antrag; der Richtpreis ist eine
// Aussage ueber eine VERGLEICHBARE Anlage). Ein Richtpreis traegt keine
// V-Nummer (version 0) und seinen Inhalt in `richtpreis_snapshot`, nie in
// `calc_snapshot`; die erste Offerte eines Projekts bleibt V1.
export const OFFER_KINDS = ["offerte", "richtpreis"] as const;
export const offerKindSchema = z.enum(OFFER_KINDS);
export type OfferKind = z.infer<typeof offerKindSchema>;
export const OFFER_KIND_LABELS: Record<OfferKind, string> = {
  offerte: "Offerte",
  richtpreis: "Richtpreis",
};

// OFF-8: der Weg, auf dem eine Version zum Kunden ging (offers.sent_channel).
export const OFFER_SENT_CHANNELS = ["email", "letter", "manual"] as const;
export const offerSentChannelSchema = z.enum(OFFER_SENT_CHANNELS);
export type OfferSentChannel = z.infer<typeof offerSentChannelSchema>;
export const OFFER_SENT_CHANNEL_LABELS: Record<OfferSentChannel, string> = {
  email: "per E-Mail",
  letter: "per Brief",
  manual: "von Hand als versendet markiert",
};

// STABIL-8: Es gibt keinen Schritt-Status mehr. Ein Arbeitsschritt ist
// erledigt, wenn `project_steps.completed_at` gesetzt ist — sonst nicht. Die
// vier Werte `pending/active/done/skipped` beschrieben eine Spalte, die nie
// jemand geschrieben hat (Migration 0134), und die Etiketten waren zuletzt
// nur noch im Styleguide zu sehen. Ein Wortschatz fuer einen Zustand, den es
// nicht gibt, ist die naechste Falle derselben Art.

// Rechnungs-Status
export const INVOICE_STATUSES = [
  "draft",
  "sent",
  "partial",
  "paid",
  "overdue",
  "cancelled",
] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

// Lieferanten-Rechnungs-Status (Phase 5.25). Spiegelt bexio `kb_bill`-States
// in eine OS-eigene Enum-Form. Kein 'sent' (bexio-Seite, fuer uns nicht
// relevant), kein 'overdue' (wird dynamisch aus `dueAt < now()` abgeleitet,
// genau wie bei `invoices`).
export const BILL_STATUSES = [
  "draft",
  "open",
  "partial",
  "paid",
  "cancelled",
] as const;
export const billStatusSchema = z.enum(BILL_STATUSES);
export type BillStatus = z.infer<typeof billStatusSchema>;

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  draft: "Entwurf",
  open: "Offen",
  partial: "Teilzahlung",
  paid: "Bezahlt",
  cancelled: "Storniert",
};

// Produktgruppen (Baustein ENG-1). OS-eigenes Feld auf `products` — bexio hat
// keinen funktionierenden Gruppen-Endpoint (article_group liefert 404 in der
// Bergler-Instanz, verifiziert 2026-07-03). NULL in der DB = "Unzugeordnet"-
// Eingang fuer frisch gesyncte bexio-Artikel. text + zod-Enum, gleiche
// Konvention wie OFFER_STATUSES.
export const PRODUCT_GROUPS = [
  "pv",
  "bess",
  "charging",
  "wp",
  "services",
] as const;
export const productGroupSchema = z.enum(PRODUCT_GROUPS);
export type ProductGroup = z.infer<typeof productGroupSchema>;

// Labels exakt nach Mockup 2026-07-03-produkte-menue.html (1:1-Leitplanke).
export const PRODUCT_GROUP_LABELS: Record<ProductGroup, string> = {
  pv: "Photovoltaik",
  bess: "Batteriespeicher",
  charging: "Ladeinfrastruktur",
  wp: "Wärmepumpe",
  services: "Dienstleistungen",
};

// Rueckschreib-Queue-Status fuer bexio-Produkte (ENG-1 Schema, ENG-3 Logik).
// NULL = synchron; 'pending' = OS-Edit wartet auf Push; 'error' = Push schlug
// fehl (UI zeigt den "→ bexio"-Chip bis der Status wieder NULL ist).
export const BEXIO_PUSH_STATUSES = ["pending", "error"] as const;
export const bexioPushStatusSchema = z.enum(BEXIO_PUSH_STATUSES);
export type BexioPushStatus = z.infer<typeof bexioPushStatusSchema>;

// Preis-Modus je Produkt (Baustein PS-2). Steuert, wie der Reprice den UVP aus
// dem EK bildet:
//   'percent'      = %-Gesetz (Regelfall)      UVP = EK / (1 − Marge)
//   'module_fixed' = Modul-Fixaufschlag (2.7)  UVP = EK + Aufschlag (von Boeden
//                    ausgenommen, damit teure Module nicht durch die Prozente
//                    unnoetig teurer werden)
//   'exempt'       = aus dem Kategorie-Reprice AUSGENOMMEN: Dienstleistungen +
//                    lokale Leistungen (eigene Logik = PS-3) sowie punktuell ein
//                    zurueckgehaltenes Produkt mit verdaechtigem EK.
// OS-eigenes Feld auf `products`, text + DB-CHECK (Konvention wie PRODUCT_GROUPS).
export const PRODUCT_PRICE_MODES = [
  "percent",
  "module_fixed",
  "exempt",
] as const;
export const productPriceModeSchema = z.enum(PRODUCT_PRICE_MODES);
export type ProductPriceMode = z.infer<typeof productPriceModeSchema>;

// Interaction-Actions (Ranking-Signal)
export const INTERACTION_ACTIONS = ["view", "edit", "create"] as const;
export const interactionActionSchema = z.enum(INTERACTION_ACTIONS);
export type InteractionAction = z.infer<typeof interactionActionSchema>;

// Interaction-Entity-Types
export const INTERACTION_ENTITY_TYPES = ["customer", "project"] as const;
export const interactionEntityTypeSchema = z.enum(INTERACTION_ENTITY_TYPES);
export type InteractionEntityType = z.infer<typeof interactionEntityTypeSchema>;

// Audit-Actions
export const AUDIT_ACTIONS = ["create", "update", "delete"] as const;
export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

// Project-Event-Kind
// Persistenter State der OneDrive-Folder-Anlage (Phase 5.44, Migration 0050).
// Auspraegungen + Bedeutung siehe Migration 0050; UI-Differenzierung in
// `ProjectDocuments.FolderPreparingBanner`. text + zod-Enum (kein DB-CHECK),
// damit neue States ohne Migration moeglich sind.
export const ONEDRIVE_CREATE_STATES = [
  "pending",
  "created",
  "skipped_lead",
  "skipped_no_code",
  "failed",
  // FB-A19: der Projekt-Ordner steht, aber mindestens ein Template-Ordner
  // fehlt. Vorher war dieser Fall von 'created' NICHT zu unterscheiden — die
  // Einzel-Fehler wurden pro Ordner abgefangen, der Gesamtlauf galt trotzdem
  // als erfolgreich und schrieb 'created'. Genau deshalb ist es niemandem
  // aufgefallen. Der Waechter `onedrive-template-health` liest diesen Zustand.
  "template_incomplete",
  // ORDNER-1: es wurde bewusst KEIN Ordner angelegt, weil ein Mensch waehlen
  // muss (ein Ordner derselben Projektnummer gehoert einem anderen Projekt;
  // der im Assistenten gewaehlte Ordner war nicht mehr frei). Kein Ausfall —
  // `onedrive_create_error` traegt den Satz fuer ALLE, nicht nur fuer Admins.
  "ordner_waehlen",
] as const;
export const onedriveCreateStateSchema = z.enum(ONEDRIVE_CREATE_STATES);
export type OnedriveCreateState = z.infer<typeof onedriveCreateStateSchema>;

export const PROJECT_EVENT_KINDS = [
  "status_change",
  "note",
  "file_added",
  "bexio_sync",
  "grok_insight",
  "assignment",
  // LEAD-19 — der Ansprechpartner wurde gesetzt oder gewechselt (die Rolle
  // VOR dem Projektleiter: wer dem Kunden gegenuebersteht, ab dem Import).
  // Payload: fromUserId, toUserId, source (setting | manual_create |
  // distribution | self | backfill | evidence). LEAD-20: `evidence` = der
  // Beleg-Sammler hat gewechselt (dazu evidenceId, evidenceOccurredAt und
  // der Satz `reason` fuer die Zeitleiste).
  "contact_person_assigned",
  // Phase 5.92 — Stufige Lead-Pipeline. Drei Pipeline-Marker als
  // ProjectEvents, damit Projector + Timeline + Audit denselben Kanal nutzen.
  // `lead_accepted_by_pl`  — PL hat den zugewiesenen Lead angenommen.
  // `customer_contacted`   — PL hat den Kunden zum ersten Mal kontaktiert
  //                          (Status-Wechsel lead -> lead_in_contact).
  // `project_start_email_sent` — PL hat die Projekt-Start-Mail mit Magic-Link
  //                          versendet (Status-Wechsel lead_in_contact -> offered).
  "lead_accepted_by_pl",
  "customer_contacted",
  "project_start_email_sent",
  // Phase 5.101 — Soft-Delete-Event. Wird vom ProjectService.softDelete
  // geschrieben, bevor der Status-Flip auf 'deleted' passiert. Erlaubt
  // Audit-Spur ueber wer-wann-warum geloescht hat.
  "project_deleted",
  // OD-3b — Wiederherstellung eines soft-geloeschten Projekts
  // (ProjectService.restore): Status-Flip zurueck auf den im
  // project_deleted-Payload festgehaltenen Vorher-Status. Spiegel-Event zu
  // project_deleted (Undo-Pfad des Aufraeum-Automaten).
  "project_restored",
  // LEAD-21 — der Kunde hat an eine geschlossene (geloeschte oder abgesagte)
  // Anfrage geschrieben; das OS hat sie wieder geoeffnet. Payload: from
  // (deleted|rejected), to, evidenceId, source, occurredAt, reason. Quelle der
  // Karte «hat wieder geschrieben» (portal_message_received, kind
  // `wieder_geschrieben`).
  "lead_reopened_by_customer",
  // Phase 6.281 — Diese drei Kinds wurden seit jeher real in
  // `project_events.kind` geschrieben (Spalte ist `text`, kein DB-Enum),
  // fehlten aber hier → `EVENT_META` (Record<ProjectEventKind,…>) kannte sie
  // nicht → ProjectTimeline crashte auf `undefined.icon`. Jetzt im Enum, damit
  // Record-Vollstaendigkeit + ProjectEvent.kind sie compile-time erzwingen.
  // `first_contact_mail_sent`   — services/firstContactMail.ts (Lead-Erstkontakt).
  // `customer_status_mail_sent` — services/customerStatusMail.ts.
  // `calc_reset`                — services/calc.ts (Kalkulation zurueckgesetzt).
  "first_contact_mail_sent",
  "customer_status_mail_sent",
  // 27.07.2026 — «Ohne Mail fortfahren», der zweite Weg im damaligen
  // Freigabe-Fenster. HISTORISCH: seit KUND-2 (17.08.2026) schreibt niemand
  // diese Art mehr — das Fenster ging mit KUND-1, der Knopf mit KUND-2. Die
  // Art bleibt im Enum, weil drei Bestandszeilen sie tragen und der
  // Projekt-Verlauf sie weiterhin anzeigen koennen muss.
  "customer_mail_declined",
  "calc_reset",
  // ENG-11 — Rechnungskette. Zwei Marker, damit der Projekt-Verlauf die
  // Auftrags-/Rechnungs-Realitaet zeigt statt nur das Audit-Log:
  // `order_confirmed` — PL hat den Auftrag bestaetigt (OfferService.confirmOrder).
  // `invoice_created` — bexio-Rechnung aus dem Auftrag erstellt
  //                     (BexioInvoicePipelineService Stage A).
  // G-15 — Stufe B/C bekommen eigene Marker: der Verlauf zeigt die volle
  // Rechnungskette, und der Wiedereinstiegs-Banner + Cron-Backstop erkennen
  // an `invoice_sent` (bzw. dessen Fehlen) eine unvollstaendige Pipeline.
  "order_confirmed",
  "invoice_created",
  "invoice_issued",
  "invoice_sent",
  // G-12 — Brief-Versand gehoert zur Status-Wahrheit: erfolgreicher
  // pingen-Versand einer Offerte (PingenLetterService.sendOfferAsLetter)
  // flippt draft→sent und hinterlaesst diesen Timeline-Marker.
  "offer_letter_sent",
  // G-12 (absorbiert Alt-4.5) — Zustell-Rueckmeldung: pingen meldet den
  // Brief als zugestellt (PingenStatusService.applyStatusUpdate, Webhook +
  // Poll-Cron — EIN Update-Pfad). Stilles Timeline-Event am Projekt.
  "letter_delivered",
  // G-5 — Versand-Wahrheit am Projekt: «wann ging welche Version an wen»
  // stand bisher nur im Audit-Log auf /system. Beide Dispatch-Pfade in
  // offerMail.ts schreiben jetzt Timeline-Events (deckt zugleich die
  // H-6-Mini-Vorstufe offer_mail_sent ab).
  "offer_email_sent",
  "order_confirmation_email_sent",
  // G-5 — Auto-Vorstellungs-Mail bei PL-Zuweisung (customerAutoMail.ts):
  // der Kunde lernt seinen (neuen) Ansprechpartner automatisch kennen.
  "pl_intro_mail_sent",
  // H-6 Schritt 2 — projekt-gematchte eingehende Kunden-Mail (Poll-Cron →
  // Datenschutz-Filter → store). Payload traegt inboundMailId/from/subject/
  // matchKind; geschrieben wird NUR beim Erst-Insert (message_id UNIQUE),
  // Re-Polls erzeugen kein zweites Event.
  "inbound_mail_received",
  // BELEG-1 Teil 1 — eine Mail aus einem Team-Postfach an die Kundenadresse
  // (Gesendet-Ordner). Der Beleg, dass geantwortet/verschickt wurde.
  "team_mail_sent",
  // G-20 — Projekt-Verlauf. Sechs Kinds, damit der Dossier-Verlauf die
  // bisher stillen Ereignisse zeigt:
  // `calc_adjusted`       — Kalkulation gespeichert, EIN Event pro Speichern
  //                         (CalcService-Mutationspfade). Payload traegt
  //                         totalVkBefore/totalVkAfter (inkl. MwSt) +
  //                         positionsChanged.
  // `calc_section_added`  — Gewerk-Block kam dazu (AddBlockBar-Merge oder
  //                         Kombi-Erweiterung); Payload zusaetzlich
  //                         blocksAdded (Seed-Kinds, z.B. ["pv"]).
  // `step_completed` /
  // `step_reopened`       — Arbeitsschritt abgehakt bzw. wieder geoeffnet
  //                         (ProjectStepService.markCompleted, nur bei echtem
  //                         Uebergang). Payload: stepId + title.
  // `mail_send_blocked`   — Mail-Versand blockiert (reportMailSendBlocked;
  //                         bisher nur Notification, jetzt auch Verlaufszeile).
  // `mail_rejected`       — G-19-Freigabe-Modal: PL hat eine vorbereitete
  //                         Mail VERWORFEN. Kind + Meta sind hier definiert;
  //                         der Emitter kommt mit dem G-19-Modal.
  "calc_adjusted",
  "calc_section_added",
  "step_completed",
  "step_reopened",
  // SCHRITT-1 (Marvin 26.08.2026) — wer einen externen Schritt gemacht hat,
  // wurde erfasst, geaendert oder weggenommen. EINE Zeile pro Handgriff (auch
  // bei «Alle → Extern»). Payload: name (null = entfernt), steps[{stepId,
  // title, previousName}].
  "step_extern_named",
  // SCHRITT-4 (Corsin 07.10.2026) — ein Schritt ging an eine ANDERE Person als
  // den, der klickt (oder sie wurde weggenommen). EINE Zeile pro Handgriff.
  // Payload: names[] (leer = entfernt), assignees[], steps[{stepId, title}].
  "step_assigned",
  // KUND-5 (Marvin 07.10.2026) — das OS hat der Anfrage ihren Kunden gegeben:
  // bestehenden zugeordnet (autoCreated false, matchMode) oder neu angelegt.
  "customer_linked_from_lead",
  "mail_send_blocked",
  "mail_rejected",
  // H-6 Schritt 3 — eBau-Behoerden-Mail (noreply@are.gr.ch) einem Projekt
  // zugeordnet (MAIL-2: per Dossier-Nummer, sonst Gemeinde mit genau einem
  // Kandidaten, sonst per Karte/Hand nachtraeglich). Payload: dossierNr,
  // gemeinde, eventKind (uebermittelt/sonstig — Bewilligung und Ablehnung
  // haben seit MAIL-2 eigene Kinds), link, matchBasis. Idempotenz pro
  // (Dossier-Nr, Art): pro Dossier und Art genau EIN Event.
  "ebau_notice_received",
  // MAIL-2 (Daniel 2026-09-15) — die Dossier-Nummer wurde am Projekt
  // hinterlegt oder entfernt. Payload: dossierNr (null = entfernt),
  // previousDossierNr, instanceId, source (ordner | hand | karte | meldung),
  // bei `ordner` zusaetzlich fileName (die Quittung, aus der sie stammt).
  "ebau_dossier_set",
  // MAIL-2 — der Bauentscheid aus eBau, als eigene Verlaufszeile sichtbar:
  // «Baubewilligung erteilt — Dossier …, Link» bzw. «Baugesuch abgelehnt».
  // Bewusst KEIN Arbeitsschritt `baubewilligung` (haette Verdienst-
  // Gewichtung, kommt in M5c). Payload: dossierNr, gemeinde, link,
  // receivedAt, matchBasis.
  "ebau_bewilligung",
  "ebau_ablehnung",
  // TRANSPARENZ-6 — der Ordner-Scan hat eine Dossier-Nummer gefunden, die
  // schon an einem anderen Projekt steht, und nichts geschrieben. Payload:
  // dossierNr, otherProjectId, fileName, satz.
  "ebau_dossier_conflict",
  // FB-M3 (Marvin 2026-07-27) — Material aus dem eigenen Lager, erfasst bzw.
  // zurueckgenommen (ProjectStockWithdrawalService, Migration 0125). Eigenes
  // Kind mit Absicht: eine Lager-Entnahme ist KEIN Beleg und darf im Verlauf
  // nie wie einer aussehen. Payload: withdrawalId, productName, productSku,
  // quantity, unit, unitCostChf, amountChf.
  "stock_withdrawal_added",
  "stock_withdrawal_removed",
  // RO-5 (Daniel 2026-07-27) — die Dach-Auskunft wurde von einem MENSCHEN
  // freigegeben bzw. bewusst zurueckgehalten. Zwei Kinds und nicht eines:
  // «raus an den Kunden» und «bewusst nicht raus» sind zwei verschiedene
  // Ereignisse, und der zweite Fall ist der, der ohne Verlaufszeile still
  // verschwaende. Payload: toEmail, declineReason, decidedAt, freigabe.
  "roof_document_sent",
  "roof_document_declined",
  // Prinzip 19 (Daniel 2026-10-08, «kein automat in dem OS darf eine Blackbox
  // für den nutzer sein») — der Richtpreis-Automat hat entschieden und NICHT
  // gesendet. Bis dahin stand der Grund nur in `roof_document_dispatches` und
  // an der Karte; im Verlauf des Vorgangs fehlte er. Payload: reason (der
  // benannte Grund aus `shared/roof/dispatch.ts`), uncertainties, trigger.
  // Der Versand selbst bleibt `roof_document_sent` (freigabe: automat).
  "roof_automat_decision",
  // HOME-14 (Daniel-Entscheid 2026-07-31) — der Aufraeum-Automat hat diesen
  // Lead als Dublette geschlossen. Bis dahin stand die Begruendung NUR in der
  // Meldung `lead_duplicate_auto_closed` und im Reason-Text des
  // `status_change`-Events; `folderRefsMoved` stand nirgends im Verlauf. Die
  // Meldung faellt mit HOME-14 weg — die Begruendungspflicht darf das nicht.
  // Payload: leadingProjectId, leadingLabel, reason, protocol, folderRefsMoved.
  "lead_duplicate_auto_closed",
  // LEAD-7 Teil 3 (Daniel 2026-07-31) — der Rueckkanal aus dem Kundenportal.
  // Der Kunde KONNTE schon immer tippen (`/api/portal/edit` schreibt
  // customerNotes + Wunschtermin aufs Projekt), aber es gab weder ein
  // Ereignis noch eine Meldung: die Nachricht landete still in einer Spalte.
  // Zwei Kinds und nicht eines — eine Frage und ein Terminwunsch sind
  // verschiedene Dinge und muessen im Verlauf verschieden heissen.
  // `portal_message_received`      — Payload: excerpt, length, sessionId.
  // `portal_appointment_requested` — Payload: appointmentAt, note, sessionId.
  // Quelle ist in beiden Faellen `mail` (EVENT_SOURCE): es kam von aussen,
  // vom Kunden — genau das, was WB-5 auf den Kacheln als Bewegung zaehlt.
  "portal_message_received",
  "portal_appointment_requested",
  // LEAD-7 Teil 2 (Daniel 2026-07-31) — der Kunde hat eine FEHLENDE ANGABE
  // nachgetragen, nach der der Kundenlink gefragt hat (Adresse und/oder
  // Jahresverbrauch). Eine eigene Art und kein `portal_message_received`,
  // weil «Kunde hat die Adresse nachgetragen» sich anders liest als «Kunde
  // hat geschrieben» — und weil im Verlauf stehen muss, WAS nachgereicht
  // wurde. Payload: fields (die nachgetragenen Feldnamen), address,
  // annualKwh, sessionId.
  "portal_details_supplied",
  // OFF-12 (Daniel 09.09.2026) — der Kunde hat im Portal eine FRAGE an den
  // Projektleiter gestellt (Payload: text, length, sessionId — der Wortlaut
  // steht in der Karte, die Antwort kommt per Mail oder Telefon, kein Chat)
  // oder FOTOS fuer den Festpreis hochgeladen (Payload: kinds, sessionId —
  // der Schnellweg «zwei Fotos statt Termin»).
  "portal_question_received",
  "portal_photos_received",
  // LEAD-17 (Daniel 08.09.2026) — das OS hat einen BELEG gefunden, dass ein
  // Mensch mit dem Lead zu tun hatte: eine Mail aus einem Team-Postfach an
  // die Lead-Adresse, eine Antwort des Kunden, ein bexio-Kontakt nach dem
  // Lead-Eingang. Payload: evidenceId, source, direction, occurredAt,
  // mailbox, description («Marvin hat dem Kunden am 12.08.2026 geschrieben»).
  // Kein Betreff, kein Text — der Beleg ist ein Fakt mit Datum, nicht ein
  // Inhalt.
  "lead_contact_evidence",
  // ABS-1 (Corsin + Daniel 2026-08-11) — der Abschluss-Vorgang, beide
  // Richtungen. Zwei Kinds und nicht eines: «ich bin fertig» und «so nicht»
  // sind verschiedene Ereignisse, und das zweite ist das, dessen Begruendung
  // im Verlauf stehen MUSS — sonst weiss in drei Monaten niemand mehr, warum
  // ein Projekt zweimal angemeldet wurde.
  // `completion_requested` — Payload: requestedBy, note, stepsCompleted,
  //                          stepsTotal, invoicesPaid, invoicesTotal, isReady.
  // `completion_rejected`  — Payload: rejectedBy, reason, requestedBy.
  "completion_requested",
  "completion_rejected",
  // ABS-3 (Daniel 2026-09-04) — das Bonus-Jahr wurde festgelegt: bei der
  // Freigabe (via "approval") oder danach korrigiert (via "correction"). EIN
  // Kind fuer beide Einstiege, weil es EIN Vorgang ist — «welches Jahr zaehlt
  // fuer den Bonus» — und die Lineage vom Jahres-Total zum Ereignis eine
  // Antwort haben soll, nicht zwei. Payload: bonusYear, proposedYear,
  // previousBonusYear, reason, via, setBy.
  "bonus_year_set",
  // KALK-7 (Sandro 2026-08-20, Daniel 2026-09-16) — ein Mensch steht fuer die
  // Excel-Kalkulation aus dem Ordner ein («Excel-Wert übernehmen»), nimmt es
  // zurueck, oder die Bestaetigung verfaellt, weil die Datei andere Werte
  // traegt. EIN Kind fuer die drei Richtungen, wie `bonus_year_set`.
  // Payload: action (bestaetigt | zurueckgenommen | verfallen), fileName,
  // nettoChf, db1Chf, description (der Satz).
  "excel_calc_confirmation",
  // KALK-9 — der Projektleiter hat die Quelle der Nachkalkulation gewaehlt
  // (Excel-Datei, Kalkulation im OS, nur Rechnungen und Belege). Payload:
  // quelle, vorher, description.
  "nachkalk_quelle_gewaehlt",
  // KALK-18 — Verkaufspreis von Hand gesetzt / zurueckgenommen.
  "verkauf_hand_set",
  // KALK-16 — «Nachkalkulation mit OneDrive abgleichen»: die Kalkulation wurde
  // aus der Excel-Datei uebernommen (action uebernommen) oder die Uebernahme
  // zurueckgenommen (rueckgaengig). Payload: action, description, fileName,
  // Summen, versionId.
  "excel_calc_imported",
  // BONUS-1 (Daniel 2026-09-26) — die Bonus-Grundlage wurde festgelegt: DB
  // oder Anteile von Hand, zurueck auf die Rechnung, Schritte uebernommen,
  // bei der Freigabe eingefroren, neu uebernommen, oder ein Abschluss aus der
  // Uebernahme freigegeben. EIN Kind fuer alle Richtungen (wie
  // `excel_calc_confirmation`). Payload: action, description (der Satz),
  // reason, setBy.
  "bonus_basis_set",
  // ORDNER-1 (10.10.2026) — ein Mensch hat dem Projekt einen bestehenden
  // OneDrive-Ordner zugewiesen («Anderen Ordner verknüpfen»).
  // Payload: satz, ordnerName, phase, vorherOrdnerName, codeVorher, codeNeu.
  "folder_linked",
  // ORDNER-2 (11.10.2026) — zwei Projekte zusammengefuehrt. Steht an BEIDEN
  // Projekten; der Verweis auf das Gegenueber IST der Zeiger (keine Spalte).
  // Payload: rolle ('quelle'|'ziel'), anderesProjectId, anderesLabel, satz,
  // gewandert (Zaehler + rechnungIds + schritte).
  "project_merged",
  // ORDNER-2 — «diese zwei sind vermutlich dasselbe Vorhaben», von einem
  // Menschen oder einer Session gesetzt (kein Automat). Haengt am Projekt, das
  // aufgehen soll. Payload: zielProjectId, grund, vonUserId.
  "merge_suggested",
  // NACHFASS-2 (17.10.2026) — das OS hat den Projektleiter per Mail gefragt,
  // ob nachgefasst ist (EIN Eintrag beim ersten Mal je Versand; jede weitere
  // Mail steht in `nachfass_mail_versand`). Payload: offerId, anUserId,
  // anName, arbeitstage, satz, at.
  "nachfass_frage",
  // NACHFASS-2 — die Antwort auf die Frage, im OS bestaetigt. Payload:
  // antwort ('ja'|'nein'), stand (der Satz «Was ist der Stand?», optional),
  // offerId, satz.
  "nachfass_antwort",
] as const;
export const projectEventKindSchema = z.enum(PROJECT_EVENT_KINDS);
export type ProjectEventKind = z.infer<typeof projectEventKindSchema>;

// G-20 — Projekt-Verlauf: Kinds, die NIE als project_events-Zeile existieren,
// sondern beim Aggregat-Read synthetisiert werden (ProjectHistoryService):
// `project_created` aus projects.createdAt (erste Verlaufszeile), `folder_moved`
// aus kuratierten audit_log-Zeilen (action onedrive_folder_move).
export const PROJECT_HISTORY_SYNTHETIC_KINDS = [
  "project_created",
  "folder_moved",
] as const;
export const projectHistoryKindSchema = z.enum([
  ...PROJECT_EVENT_KINDS,
  ...PROJECT_HISTORY_SYNTHETIC_KINDS,
]);
export type ProjectHistoryKind = z.infer<typeof projectHistoryKindSchema>;

// Deutsche Labels pro Status — Single Source of Truth fuer UI, Dropdowns,
// Filter, CSV-Exports, Grok-Prompts. Presentation-Layer (Badge-Tone) bleibt
// in der jeweiligen Komponente.
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  lead: "Lead",
  lead_in_contact: "Im Kontakt",
  offered: "Offeriert",
  pending: "Pendent",
  won: "Gewonnen",
  in_progress: "In Arbeit",
  completed: "Abgeschlossen",
  rejected: "Abgelehnt",
  archived: "Archiv",
  // Phase 5.101 — Soft-Delete-Marker. Projekt taucht in keinem Tab auf;
  // Label bleibt fuer Admin-Recovery-Faelle existent.
  deleted: "Gelöscht",
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: "Entwurf",
  sent: "Versendet",
  partial: "Teilzahlung",
  paid: "Bezahlt",
  overdue: "Überfällig",
  cancelled: "Storniert",
};

// MAIL-1 — in welchem Ordner eines Team-Postfachs eine gespeicherte
// Kundenmail lag. Spalte `inbound_mails.folder` (Migration 0170) und das
// Feld der Karte «Mail im Junk-Ordner» lesen dieselbe Liste.
export const INBOUND_MAIL_FOLDERS = ["inbox", "junk"] as const;
export type InboundMailFolder = (typeof INBOUND_MAIL_FOLDERS)[number];
/**
 * BELEG-1 Teil 1 — was `inbound_mails.folder` tragen kann: die beiden
 * Eingangs-Ordner des Polls PLUS die gesendeten Mails (Migration 0188). Der
 * Poll und seine Cursor-Schluessel kennen weiter nur `InboundMailFolder`.
 */
export const INBOUND_MAIL_STORED_FOLDERS = [...INBOUND_MAIL_FOLDERS, "sentitems"] as const;
export type InboundMailStoredFolder = (typeof INBOUND_MAIL_STORED_FOLDERS)[number];
